// Domain model + pure helpers for the Enquiry Dashboard (Customer Inquiry &
// Sales-Order register), ported from the standalone prototype. No seed data —
// the dropdown vocabularies below are the only fixed lists. Data will come from
// the backend once endpoints are wired.

export const ENQ_CATEGORIES = ['Customization', 'Architect', 'Shop-in-Shop', 'Retailer', 'Distributor'] as const;
export const ENQ_CHANNELS = ['Phone', 'WhatsApp', 'Email', 'Showroom Visit', 'Other'] as const;
export const ENQ_STATUSES = [
  'Enquiry Received',
  'Pending Sales Head Approval',
  'Approved — Stock Check',
  'Sales Order Generated',
  'Rejected by Sales Head',
  'Rejected — Out of Stock',
] as const;
export const ENQ_COMM_TYPES = ['Call', 'WhatsApp', 'Email', 'Site visit', 'Stock/price check', 'Other'] as const;

export type EnqCategory = (typeof ENQ_CATEGORIES)[number];
export type EnqChannel = (typeof ENQ_CHANNELS)[number];
export type EnqStatus = (typeof ENQ_STATUSES)[number];
export type EnqCommType = (typeof ENQ_COMM_TYPES)[number];
export type RangeKey = 'week' | 'month' | 'year' | 'all';

/** Statuses a salesperson may set by hand (pre-review). Everything past review
 *  is driven by the operational pipeline and derived on the server. */
export const REP_SETTABLE_STATUSES: EnqStatus[] = ['Enquiry Received', 'Pending Sales Head Approval', 'Rejected by Sales Head', 'Rejected — Out of Stock'];

/** Statuses still in the active pipeline (not SO-generated, not rejected). */
export const PENDING_STATUSES: EnqStatus[] = ['Enquiry Received', 'Pending Sales Head Approval', 'Approved — Stock Check'];
export const REJECTED_STATUSES: EnqStatus[] = ['Rejected by Sales Head', 'Rejected — Out of Stock'];
export const SO_STATUS: EnqStatus = 'Sales Order Generated';

export interface EnqComm {
  id: string;
  date: string; // yyyy-mm-dd
  type: EnqCommType;
  by: string;
  note: string;
}

/** One line of the enquiry's items table. */
export interface EnqItem {
  id: string;
  itemId: number | null; // Item Master id, or null when free-typed
  item: string;          // item / product name (also the description)
  qty: number;
  unit: string;
  rate: number;
}

export interface Enquiry {
  id: string;
  date: string; // yyyy-mm-dd — when the inquiry came in
  partyId: number | null; // existing Party customer
  customer: string;
  category: EnqCategory;
  channel: EnqChannel;
  assignedTo: string;
  items: EnqItem[];
  // Summary fields derived from `items` on save — kept so the dashboard table,
  // KPIs and rollups don't each have to recompute them.
  item: string;
  qty: number;
  status: EnqStatus;
  valueInr: number;
  salesOrderNo: string;
  approvedBy: string;
  notes: string;
  comms: EnqComm[];
  /** Operational enquiry number (set once persisted). */
  enqNo?: string;
  /** Operational pipeline stage (new/reviewed/do-created/…). Drives the CRM
   *  status once the enquiry leaves 'new'. */
  stage?: string;
}

/** Amount for one line. */
export const enqLineAmount = (l: EnqItem): number => (Number(l.qty) || 0) * (Number(l.rate) || 0);

/** Roll an items table up into the Enquiry summary fields. */
export function summarizeItems(items: EnqItem[]): { item: string; qty: number; valueInr: number } {
  const named = items.filter((l) => l.item.trim());
  const item = named.length
    ? named[0].item.trim() + (named.length > 1 ? ` +${named.length - 1} more` : '')
    : '';
  return {
    item,
    qty: items.reduce((s, l) => s + (Number(l.qty) || 0), 0),
    valueInr: items.reduce((s, l) => s + enqLineAmount(l), 0),
  };
}

export const isPending = (e: Enquiry): boolean => PENDING_STATUSES.includes(e.status);
export const isRejected = (e: Enquiry): boolean => REJECTED_STATUSES.includes(e.status);
export const isSo = (e: Enquiry): boolean => e.status === SO_STATUS;

export function inRange(dateStr: string, range: RangeKey): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  if (range === 'week') {
    const w = new Date(now);
    w.setDate(now.getDate() - 7);
    return d >= w && d <= now;
  }
  if (range === 'month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  if (range === 'year') return d.getFullYear() === now.getFullYear();
  return true;
}

export const filterByRange = (rows: Enquiry[], range: RangeKey): Enquiry[] => rows.filter((e) => inRange(e.date, range));

const commDates = (e: Enquiry): string[] => (e.comms ?? []).map((c) => c.date).sort();
export const firstContact = (e: Enquiry): string => { const d = commDates(e); return d.length ? d[0] : e.date; };
export const lastUpdate = (e: Enquiry): string => { const d = commDates(e); return d.length ? d[d.length - 1] : e.date; };
export const commCount = (e: Enquiry): number => (e.comms ?? []).length;

export interface EnqStats {
  total: number;
  pendingCount: number;
  soCount: number;
  rejectedCount: number;
  conversionRate: number;
  totalValue: number;
}

export function computeStats(rows: Enquiry[]): EnqStats {
  const total = rows.length;
  const so = rows.filter(isSo);
  return {
    total,
    pendingCount: rows.filter(isPending).length,
    soCount: so.length,
    rejectedCount: rows.filter(isRejected).length,
    conversionRate: total ? Math.round((so.length / total) * 100) : 0,
    totalValue: so.reduce((s, e) => s + e.valueInr, 0),
  };
}

export interface CategoryRow {
  category: EnqCategory;
  total: number;
  pending: number;
  so: number;
  rejected: number;
  rate: number;
}

export function categoryBreakdown(rows: Enquiry[]): CategoryRow[] {
  return ENQ_CATEGORIES.map((category) => {
    const list = rows.filter((e) => e.category === category);
    const so = list.filter(isSo).length;
    return {
      category,
      total: list.length,
      pending: list.filter(isPending).length,
      so,
      rejected: list.filter(isRejected).length,
      rate: list.length ? Math.round((so / list.length) * 100) : 0,
    };
  });
}

export interface PersonRow {
  name: string;
  orders: number;
  pending: number;
  so: number;
  rate: number;
  /** Sales-order value this member has closed (₹). */
  salesValue: number;
}

export function personStats(rows: Enquiry[], person: string): PersonRow {
  const list = rows.filter((e) => e.assignedTo === person);
  const soList = list.filter(isSo);
  const so = soList.length;
  return {
    name: person,
    orders: list.length,
    pending: list.filter(isPending).length,
    so,
    rate: list.length ? Math.round((so / list.length) * 100) : 0,
    salesValue: soList.reduce((s, e) => s + e.valueInr, 0),
  };
}

export function byChannel(rows: Enquiry[]): { name: EnqChannel; value: number }[] {
  return ENQ_CHANNELS.map((name) => ({ name, value: rows.filter((e) => e.channel === name).length })).filter((x) => x.value > 0);
}

export function byMonth(rows: Enquiry[]): { month: string; inquiries: number }[] {
  const map = new Map<string, number>();
  for (const e of rows) {
    const key = e.date.slice(0, 7);
    map.set(key, (map.get(key) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([month, inquiries]) => ({ month, inquiries }))
    .sort((a, b) => a.month.localeCompare(b.month));
}

export interface AccountRow {
  /** Group key: customer + category (also what the search matches). */
  name: string;
  customer: string;
  category: string;
  orders: number;
  so: number;
  value: number;
}

export function accountsRollup(rows: Enquiry[]): AccountRow[] {
  const map = new Map<string, AccountRow>();
  for (const e of rows) {
    const key = `${e.customer} — ${e.category}`;
    const a = map.get(key) ?? { name: key, customer: e.customer, category: e.category ?? '', orders: 0, so: 0, value: 0 };
    a.orders += 1;
    if (isSo(e)) { a.so += 1; a.value += e.valueInr; }
    map.set(key, a);
  }
  return Array.from(map.values()).sort((a, b) => b.orders - a.orders);
}

export const uid = (): string => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
